// crewbit-admin/src/components/money/ReopenRemittancePanel.tsx
//
// Slide-over form for `reopen_tax_remittance`. Owner-only — caller (parent)
// gates visibility on `useAuthStore.user.role === 'owner'`. Server also
// rejects non-owners (Hard Rule #9 server-side defense in depth).
//
// Form: reason (TextInput, required, ≥10 chars) + read-only display of
// original confirmation_number / filed_at / tax_type. Calls reopenRemittance
// (lib wrapper).
//
// On success → toast 'success' "Reopened" + close.
// On error   → toast 'error' with errorMap copy (REMITTANCE_NOT_FOUND now
//              wired correctly post-Session-136 migration 270).
//
// Spec §Tax.7.2 + §Tax.D7.

import { useState } from 'react';
import { palette, spacing, radii } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import { Toast } from '../common/Toast';
import { lookupErrorCopy } from '../../lib/errorMap';
import { reopenRemittance, type TaxType } from '../../lib/taxMutations';
import type { RemittanceLogRow } from '../../hooks/useRemittanceLog';

interface ReopenRemittancePanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** When null the panel renders nothing — guards against URL state racing
   *  ahead of remittance log fetch. */
  remittance: RemittanceLogRow | null;
}

const REASON_MIN_LENGTH = 10;

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: palette.g600,
  marginBottom: 6,
};

const readOnlyStyle: React.CSSProperties = {
  padding: '9px 12px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g150}`,
  background: palette.g50,
  fontSize: 13.5,
  color: palette.g700,
  fontVariantNumeric: 'tabular-nums',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
  minHeight: 96,
  resize: 'vertical',
};

function formatPeriod(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${s.toLocaleDateString('en-CA', opts)} – ${e.toLocaleDateString('en-CA', opts)}`;
}

function formatFiledAt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function taxTypeLabel(taxType: TaxType): string {
  return taxType.toUpperCase();
}

export function ReopenRemittancePanel({ isOpen, onClose, remittance }: ReopenRemittancePanelProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const trimmedReason = reason.trim();
  const canSubmit = !!remittance && trimmedReason.length >= REASON_MIN_LENGTH && !submitting;

  const handleSubmit = async () => {
    if (!remittance || !canSubmit) return;
    setSubmitting(true);
    const result = await reopenRemittance({
      remittanceId: remittance.id,
      reason: trimmedReason,
    });
    setSubmitting(false);

    if (result.success) {
      setToast({ msg: 'Reopened.', type: 'success' });
      setTimeout(onClose, 250);
    } else {
      setToast({
        msg: lookupErrorCopy('reopen_tax_remittance', result.error.code),
        type: 'error',
      });
    }
  };

  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="Reopen filing"
        subtitle={
          remittance
            ? `${taxTypeLabel(remittance.tax_type)} · ${formatPeriod(remittance.period_start, remittance.period_end)}`
            : undefined
        }
        width={720}
      >
        {remittance ? (
          <div style={{ display: 'grid', gap: spacing.md }}>
            <div
              style={{
                padding: spacing.md,
                background: palette.amberWash,
                border: `1px solid ${palette.amber}`,
                borderRadius: radii.sm,
                fontSize: 12.5,
                color: palette.amber,
                lineHeight: 1.45,
              }}
            >
              Reopening a filed remittance keeps the original confirmation number and
              filed-at audit trail intact, but lets you book amendments. Use sparingly
              — every reopen is logged in activity.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
              <div>
                <span style={labelStyle}>Confirmation number</span>
                <div style={readOnlyStyle}>{remittance.confirmation_number ?? '—'}</div>
              </div>
              <div>
                <span style={labelStyle}>Filed on</span>
                <div style={readOnlyStyle}>{formatFiledAt(remittance.filed_at)}</div>
              </div>
            </div>

            <div>
              <label style={labelStyle} htmlFor="rr-reason">
                Reason for reopening
              </label>
              <textarea
                id="rr-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Required. At least 10 characters — e.g. 'Caught a missed ITC from a March receipt.'"
                style={textareaStyle}
                autoFocus
              />
              <div
                style={{
                  fontSize: 11.5,
                  color: trimmedReason.length >= REASON_MIN_LENGTH ? palette.g500 : palette.amber,
                  marginTop: 4,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {trimmedReason.length} / {REASON_MIN_LENGTH} minimum
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: spacing.sm,
                marginTop: spacing.md,
                paddingTop: spacing.md,
                borderTop: `1px solid ${palette.g100}`,
              }}
            >
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '9px 16px',
                  borderRadius: radii.sm,
                  border: `1px solid ${palette.g200}`,
                  background: palette.white,
                  color: palette.g700,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{
                  padding: '9px 18px',
                  borderRadius: radii.sm,
                  border: 'none',
                  background: canSubmit ? palette.amber : palette.g300,
                  color: palette.white,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}
              >
                {submitting ? 'Reopening…' : 'Reopen filing'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: palette.g500 }}>Loading…</div>
        )}
      </SlidePanel>
      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}
